import { Box, CircularProgress } from "@mui/material";
import { useAtom } from "jotai";
import { type FC, type PropsWithChildren, useEffect } from "react";
import { createContext, useContextSelector } from "use-context-selector";
import { getRightsV2 } from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";
import { usePilotPageBackground } from "web_myplann/Infrastructure/Hooks/usePilotPageBackground";
import { rightsState } from "web_myplann/Shared/State/rightsAtom";
import {
	NO_PILOT_RIGHTS,
	type PilotRights,
} from "web_myplann/Utils/WebViewModel";

const RightsContext = createContext<PilotRights | undefined>(undefined);

const FALLBACK_FETCH_TIMEOUT_MS = 10_000;

const fetchRightsWithTimeout = async (): Promise<PilotRights> => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), FALLBACK_FETCH_TIMEOUT_MS);
	try {
		return await processResponse(() =>
			getRightsV2({ signal: controller.signal }),
		)(undefined);
	} catch {
		return NO_PILOT_RIGHTS;
	} finally {
		clearTimeout(timer);
	}
};

export const RightsProvider: FC<PropsWithChildren> = ({ children }) => {
	const [rights, setRights] = useAtom(rightsState);

	// BACKWARD COMPAT (DEV-3888): the fallback fetch and the loader below only
	// serve native shells that do not send `rights` in INIT_RESPONSE, and the
	// browser/E2E run. See useInitializeWeb for when to remove it.
	useEffect(() => {
		if (rights) {
			return;
		}
		let cancelled = false;
		fetchRightsWithTimeout().then((resolved) => {
			if (!cancelled) {
				setRights(resolved);
			}
		});
		return () => {
			cancelled = true;
		};
	}, [rights, setRights]);

	usePilotPageBackground(rights?.pilotGoal);

	if (!rights) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="100vh"
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<RightsContext.Provider value={rights}>{children}</RightsContext.Provider>
	);
};

export const useRights = <T,>(selector: (rights: PilotRights) => T): T =>
	useContextSelector(RightsContext, (rights) => {
		if (!rights) {
			throw new Error("useRights must be used within RightsProvider");
		}
		return selector(rights);
	});

export const useGoalsRight = () => useRights((rights) => rights.pilotGoal);

export const useGoalsRightOptional = () =>
	useContextSelector(RightsContext, (rights) => rights?.pilotGoal ?? false);

export const usePortfolioRightOptional = () =>
	useContextSelector(
		RightsContext,
		(rights) => rights?.pilotMyplannPortfolio ?? false,
	);
